import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import type { ComponentType } from 'react';
import { useAuth } from '../auth/AuthProvider';
import type { Rol } from '../api/types';
import { useSync } from '../sync/SyncProvider';
import { ConfirmModal } from './ConfirmModal';
import { formatRelative, initials, roleLabel } from '../lib/format';
import {
  BoxIcon,
  ChartIcon,
  CloudIcon,
  CloudOffIcon,
  InvoiceIcon,
  LogoutIcon,
  OrdersIcon,
  PanelIcon,
  ReceiptIcon,
  RefreshIcon,
  SettingsIcon,
  ShieldIcon,
  UsersIcon,
} from './icons';

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number }>;
  end?: boolean;
  soon?: boolean;
  roles?: Rol[];
}

const SECTIONS: Array<{ label: string; items: NavItem[] }> = [
  {
    label: 'Ventas',
    items: [
      { to: '/', label: 'Panel comercial', icon: PanelIcon, end: true },
      { to: '/pedidos', label: 'Pedidos y cotizaciones', icon: OrdersIcon },
      { to: '/facturas', label: 'Facturación', icon: InvoiceIcon },
      { to: '/cobranzas', label: 'Cobranzas', icon: ReceiptIcon },
      { to: '/clientes', label: 'Clientes y cartera', icon: UsersIcon },
    ],
  },
  {
    label: 'Compras',
    items: [
      { to: '/compras', label: 'Compras y pagos', icon: InvoiceIcon, roles: ['admin', 'supervisor', 'cobranza', 'auditor'] },
      { to: '/proveedores', label: 'Proveedores', icon: UsersIcon, roles: ['admin', 'supervisor', 'cobranza', 'auditor'] },
    ],
  },
  {
    label: 'Inventario',
    items: [{ to: '/inventario', label: 'Inventario', icon: BoxIcon }],
  },
  {
    label: 'Contabilidad',
    items: [
      { to: '/contabilidad', label: 'Contabilidad', icon: ChartIcon, roles: ['admin', 'supervisor', 'auditor'] },
      { to: '/reportes', label: 'Reportes', icon: ChartIcon, roles: ['admin', 'supervisor', 'auditor'] },
      { to: '/retenciones', label: 'Retenciones', icon: ReceiptIcon, roles: ['admin', 'supervisor', 'cobranza', 'auditor'] },
      { to: '/banca', label: 'Banca y conciliación', icon: ReceiptIcon, roles: ['admin', 'supervisor', 'cobranza', 'auditor'] },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { to: '/imprenta', label: 'Panel TFHKA', icon: InvoiceIcon, roles: ['admin'] },
      { to: '/usuarios', label: 'Usuarios y roles', icon: ShieldIcon, roles: ['admin'] },
      { to: '/configuracion', label: 'Configuración', icon: SettingsIcon, soon: true, roles: ['admin'] },
    ],
  },
];

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { user, demo, logout } = useAuth();
  const { online, pending, syncing, lastSync, syncNow } = useSync();
  const [logoutOpen, setLogoutOpen] = useState(false);

  const syncTone = demo
    ? 'demo'
    : !online
      ? 'offline'
      : pending > 0
        ? 'pending'
        : 'ok';

  const syncTitle = demo
    ? 'Modo demostración'
    : !online
      ? 'Sin conexión'
      : pending > 0
        ? `${pending} en cola local`
        : 'Sincronizado';

  const syncDetail = demo
    ? 'Datos locales de contingencia'
    : !online
      ? pending > 0
        ? `${pending} en cola local`
        : 'Trabajando offline'
      : pending > 0
        ? 'Pendiente de enviar'
        : `Última sync ${formatRelative(lastSync)}`;

  const handleLogout = () => {
    if (pending > 0) {
      setLogoutOpen(true);
      return;
    }
    void logout();
  };

  return (
    <aside className="sidebar" aria-label="Navegación principal">
      <div className="sidebar__brand">
        <img
          className="sidebar__isotipo"
          src="/brand/isotipo-white.png"
          alt="Isotipo GRAVENCA"
        />
        <div className="sidebar__brand-text">
          <img
            className="sidebar__wordmark"
            src="/brand/wordmark-white.png"
            alt="GRAVENCA — Cultivando futuro"
          />
        </div>
        <span className="sidebar__version">ERP 2026</span>
      </div>

      <nav className="sidebar__nav">
        {SECTIONS.map((section) => {
          const items = section.items.filter(
            (item) => !item.roles || (user !== null && item.roles.includes(user.rol)),
          );
          if (items.length === 0) return null;
          return (
          <div className="sidebar__section" key={section.label}>
            <p className="sidebar__label">{section.label}</p>
            {items.map((item) => {
              const Icon = item.icon;
              if (item.soon) {
                return (
                  <span
                    key={item.to}
                    className="nav-item nav-item--disabled"
                    aria-disabled="true"
                    title="Módulo en la siguiente fase"
                  >
                    <Icon size={18} />
                    <span className="nav-item__label">{item.label}</span>
                  </span>
                );
              }
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `nav-item${isActive ? ' nav-item--active' : ''}`
                  }
                >
                  <Icon size={18} />
                  <span className="nav-item__label">{item.label}</span>
                </NavLink>
              );
            })}
          </div>
          );
        })}
      </nav>

      <div className="sidebar__footer">
        <div className={`sync-card sync-card--${syncTone}`}>
          <div className="sync-card__top">
            <span className={`sync-dot sync-dot--${syncTone}`} aria-hidden="true" />
            <span className="sync-card__title">{syncTitle}</span>
            {syncTone === 'offline' ? <CloudOffIcon size={16} /> : <CloudIcon size={16} />}
          </div>
          <p className="sync-card__detail">{syncDetail}</p>
          {!demo ? (
            <button
              type="button"
              className="sync-card__action"
              onClick={() => void syncNow()}
              disabled={syncing || !online || pending === 0}
            >
              <RefreshIcon size={14} className={syncing ? 'spin' : undefined} />
              {syncing ? 'Sincronizando…' : 'Sincronizar ahora'}
            </button>
          ) : (
            <span className="sync-card__action sync-card__action--static">
              Los pedidos quedan en la cola local
            </span>
          )}
        </div>

        <div className="user-card">
          <span className="avatar avatar--olive" aria-hidden="true">
            {user ? initials(user.nombre) : 'GV'}
          </span>
          <div className="user-card__info">
            <strong>{user?.nombre ?? 'Invitado'}</strong>
            <span>
              {user ? roleLabel(user.rol) : 'Sin sesión'}
              {demo ? ' · Demo' : ''}
            </span>
          </div>
          <button
            type="button"
            className="icon-button icon-button--dark"
            onClick={handleLogout}
            aria-label="Cerrar sesión"
            title="Cerrar sesión"
          >
            <LogoutIcon size={18} />
          </button>
        </div>
      </div>

      {logoutOpen ? (
        <ConfirmModal
          title="Cerrar sesión con cambios pendientes"
          description={`Hay ${pending} movimiento(s) sin sincronizar. Si cierras sesión se eliminarán del dispositivo.`}
          confirmLabel="Cerrar sesión"
          tone="danger"
          onClose={() => setLogoutOpen(false)}
          onConfirm={() => {
            setLogoutOpen(false);
            void logout();
          }}
        />
      ) : null}
    </aside>
  );
}
